import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation} from '@angular/core';
import {Subject} from 'rxjs/Subject';
import {format} from 'date-fns';
import {ColumnSortService} from '../../services/column-sort/column-sort.service';
import {Subscription} from 'rxjs/Subscription';

export interface DataGridColumnFilter {
  column: string,
  value: any
}

@Component({
  selector: 'app-data-grid-column-filter',
  templateUrl: './data-grid-column-filter.component.html',
  styleUrls: ['./data-grid-column-filter.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DataGridColumnFilterComponent implements OnInit, OnDestroy {
  /**
   * Props passed into the component
   */
  @Input() props: Props;
  /**
   * Value of the filter
   * @type {any}
   */
  @Input() value: string = null;
  @Input() defaultSort;
  /**
   * Event used to pass the value of the filter
   * up the component tree
   * @type {EventEmitter<any>}
   */
  @Output() onFilterChanged = new EventEmitter();
  @Output() sort: EventEmitter<any> = new EventEmitter();
  sortClass: string;
  sortDir;
  /**
   * Subject to watch the filter change
   * @type {Subject<any>}
   */
  private filter$ = new Subject();
  /**
   * Array of subscriptions
   * @type {Array}
   */
  subs: Subscription[] = [];

  constructor(private columnSortService: ColumnSortService) {
    this.subs = [
      // subscription that emits the next filter value
      this.filter$.debounceTime(750).subscribe(e => this.onFilterChanged.emit({column: this.props.prop, value: e})),
      // subscription that manages the sort class and sort direction
      this.columnSortService.currentSort$.subscribe(data => {
        this.sortClass = this.calcSortClass(data);
      })
    ];
  }

  ngOnInit() {
    this.sortDir = this.defaultSort[0].dir;
    this.sortClass = this.calcSortClass(this.defaultSort[0]);
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  /**
   * Emits the changed filter
   * @param value
   */
  filterChanged(value) {
    // if the type is date then we need to transform our date into something usable by the application.
    this.props.options.type === 'date' && value !== null
      ? this.filter$.next(format(value, 'YYYY-MM-DD'))
      : this.filter$.next(value);
  }

  /**
   * Actions to be taken when a column is sorted
   */
  onSort(): void {
    if (!this.props.sortable) return;
    this.sortDir = this.nextSortDir(this.sortDir);
    // pushes data to the shared column sort service
    this.columnSortService.publishData({
      prop: this.props.prop,
      dir: this.sortDir
    });
    // emits the sort event
    this.sort.emit({
      column: this.props.prop,
      newValue: this.sortDir
    });

  }

  get placeholder() {
    return `Filter by ${this.props.name}...`;
  }


  /**
   * Calculates the class for the sorting icon
   * @param sort
   * @return {string}
   */
  calcSortClass(sort): string {
    // console.log('calc', sort);
    if (sort.dir === SortDirection.asc && sort.prop === this.props.prop) {
      return `sort-btn sort-asc datatable-icon-up`;
    } else if (sort.dir === SortDirection.desc && sort.prop === this.props.prop) {
      return `sort-btn sort-desc datatable-icon-down`;
    } else {
      return `sort-btn`;
    }
  }

  /**
   * Returns the next sort direction
   * @param {SortDirection} current
   * @return {SortDirection}
   */
  nextSortDir(current: SortDirection): SortDirection {
    return current === SortDirection.asc ? SortDirection.desc : SortDirection.asc;
  }
}

interface Props {
  name: string;
  prop: string;
  sortable: boolean;
  options?: Options;
}

interface Options {
  type: string;
  options?: { value: number, label: string }[];
}

enum SortDirection {
  asc = 'asc',
  desc = 'desc'
}



// WEBPACK FOOTER //
// /var/www/dnata-cerf-front-end/releases/20200326161233/src/app/modules/utilities/components/data-grid-column-filter/data-grid-column-filter.component.ts